import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, PauseCircle, PlayCircle, XCircle, CreditCard } from 'lucide-react';
import ChipEstado from '../ui/ChipEstado';
import ModalConfirmar from '../ui/ModalConfirmar';
import TiempoRelativo from '../ui/TiempoRelativo';
import { ESTADO_CLIENTE, ESTADO_SUSCRIPCION } from '../ui/estados';
import { apiPanel } from './api';

const PLANES = ['gratis', 'basico', 'negocio', 'empresa'];

// Ficha de un cliente. Todo lo accionable está aquí, y todo lo accionable
// exige motivo: `ModalConfirmar` con `pedirMotivo` es el mismo patrón que
// usa la consola de los clientes para dar de baja a alguien, y por la misma
// razón — dentro de seis meses, "por qué se suspendió a esta empresa" tiene
// que tener respuesta.
export default function FichaCliente({ id, onVolver }) {
  const [c, setC] = useState(null);
  const [error, setError] = useState(null);
  const [accion, setAccion] = useState(null);   // 'suspender'|'reactivar'|'cancelar'|'plan'
  const [planNuevo, setPlanNuevo] = useState('');

  const cargar = useCallback(() => {
    apiPanel.get(`/clientes/${id}`).then(setC).catch((e) => setError(e.message));
  }, [id]);

  useEffect(cargar, [cargar]);

  if (error) return <p className="text-sm text-red-700">{error}</p>;
  if (!c) return <p className="text-sm text-gray-400">Cargando…</p>;

  async function cambiarEstado(estado, motivo) {
    setC(await apiPanel.post(`/clientes/${id}/estado`, { estado, motivo }));
  }

  async function cambiarPlan(motivo) {
    setC(await apiPanel.post(`/clientes/${id}/plan`, { plan: planNuevo, motivo }));
  }

  const suspendida = c.estado === 'suspendida';
  const cancelada = c.estado === 'cancelada';

  return (
    <div className="space-y-5">
      <button
        onClick={onVolver}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--color-marca)] hover:text-[var(--color-marca-fuerte)]"
      >
        <ArrowLeft className="h-4 w-4" /> Clientes
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2.5">
            <h2 className="text-xl font-semibold text-gray-900">{c.nombre}</h2>
            <ChipEstado mapa={ESTADO_CLIENTE} clave={c.estado} />
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            {c.rfc || 'Sin RFC'} · {c.tipo_cuenta === 'PERSONAL' ? 'Cuenta personal' : 'Empresa'}
            {c.zona_horaria ? ` · ${c.zona_horaria}` : ''}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {!suspendida && !cancelada && (
            <Boton icono={PauseCircle} peligro onClick={() => setAccion('suspender')}>
              Suspender
            </Boton>
          )}
          {(suspendida || cancelada) && (
            <Boton icono={PlayCircle} onClick={() => setAccion('reactivar')}>
              Reactivar
            </Boton>
          )}
          {!cancelada && (
            <Boton icono={XCircle} peligro onClick={() => setAccion('cancelar')}>
              Cancelar cuenta
            </Boton>
          )}
          <Boton
            icono={CreditCard}
            onClick={() => {
              setPlanNuevo(c.plan || 'gratis');
              setAccion('plan');
            }}
          >
            Cambiar plan
          </Boton>
        </div>
      </div>

      {suspendida && (
        <Aviso tono="rojo">
          Esta empresa <strong>no puede operar</strong>. Sus usuarios pueden
          iniciar sesión y ven el motivo, pero cualquier otra petición se
          rechaza — incluidas las sesiones que ya tuvieran abiertas.
        </Aviso>
      )}
      {cancelada && (
        <Aviso tono="gris">
          Cuenta cancelada. <strong>Los datos se conservan íntegros</strong>:
          reactivarla la devuelve a la operación tal y como estaba.
        </Aviso>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Bloque titulo="Suscripción">
          <Dato etiqueta="Plan" valor={<span className="capitalize">{c.plan || '—'}</span>} />
          <Dato
            etiqueta="Estado"
            valor={c.estado_suscripcion
              ? <ChipEstado mapa={ESTADO_SUSCRIPCION} clave={c.estado_suscripcion} />
              : '—'}
          />
          <Dato etiqueta="Ciclo" valor={c.ciclo || '—'} />
          <Dato etiqueta="Periodo hasta" valor={c.periodo_fin || '—'} />
          <Dato etiqueta="Asientos incluidos" valor={c.asientos_incluidos ?? '—'} />
          <Dato etiqueta="Límite de SKU" valor={c.limite_sku ?? 'Ilimitado'} />
          <Dato etiqueta="Límite de sucursales" valor={c.limite_sucursales ?? 'Ilimitado'} />
        </Bloque>

        <Bloque titulo="Uso">
          <Dato etiqueta="Usuarios activos" valor={c.usuarios} />
          <Dato etiqueta="Sucursales" valor={c.sucursales} />
          <Dato etiqueta="Productos en catálogo" valor={c.productos} />
          <Dato
            etiqueta="Movimientos (30 días)"
            valor={
              <span className={c.movimientos_30d === 0 ? 'text-amber-700 font-semibold' : ''}>
                {c.movimientos_30d}
                {c.movimientos_30d === 0 && ' — sin actividad'}
              </span>
            }
          />
          <Dato etiqueta="Imágenes" valor={`${Number(c.imagenes_mb).toFixed(1)} MB`} />
          <Dato etiqueta="Último acceso" valor={<TiempoRelativo valor={c.ultimo_acceso} />} />
          <Dato etiqueta="Alta" valor={<TiempoRelativo valor={c.creado_en} />} />
        </Bloque>
      </div>

      <p className="text-xs text-gray-500 max-w-2xl leading-relaxed">
        «Productos» es un recuento. El catálogo, las existencias y los
        movimientos de este cliente no son accesibles desde el panel: el rol
        de base de datos con el que corre esta pantalla no tiene permiso
        sobre esas tablas.
      </p>

      {accion === 'suspender' && (
        <ModalConfirmar
          titulo={`Suspender a ${c.nombre}`}
          descripcion="Dejará de poder operar en su siguiente petición, sin esperar a que caduque su sesión."
          cambios={[{ campo: 'Estado', antes: 'Activa', despues: 'Suspendida' }]}
          aviso="Sus usuarios podrán entrar y ver el motivo, pero no registrar ni consultar nada."
          pedirMotivo
          peligro
          etiquetaConfirmar="Suspender"
          onConfirmar={(motivo) => cambiarEstado('suspendida', motivo)}
          onCerrar={() => setAccion(null)}
        />
      )}

      {accion === 'reactivar' && (
        <ModalConfirmar
          titulo={`Reactivar a ${c.nombre}`}
          descripcion="Vuelve a operar de inmediato, sin necesidad de que nadie vuelva a iniciar sesión."
          cambios={[{ campo: 'Estado', antes: ESTADO_CLIENTE[c.estado]?.etiqueta, despues: 'Activa' }]}
          pedirMotivo
          etiquetaConfirmar="Reactivar"
          onConfirmar={(motivo) => cambiarEstado('activa', motivo)}
          onCerrar={() => setAccion(null)}
        />
      )}

      {accion === 'cancelar' && (
        <ModalConfirmar
          titulo={`Cancelar la cuenta de ${c.nombre}`}
          descripcion="Cierra la cuenta. Los datos NO se borran: se conservan íntegros y la cuenta puede reactivarse."
          cambios={[{ campo: 'Estado', antes: ESTADO_CLIENTE[c.estado]?.etiqueta, despues: 'Cancelada' }]}
          aviso="Es el paso más severo del panel. Se pide escribir el nombre para que no salga por reflejo."
          pedirMotivo
          peligro
          palabraConfirmacion={c.nombre}
          etiquetaConfirmar="Cancelar cuenta"
          onConfirmar={(motivo) => cambiarEstado('cancelada', motivo)}
          onCerrar={() => setAccion(null)}
        />
      )}

      {accion === 'plan' && (
        <ModalConfirmar
          titulo={`Cambiar el plan de ${c.nombre}`}
          descripcion="Los límites se copian del catálogo de planes; no se fijan a mano."
          extra={
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">Plan nuevo</span>
              <select
                value={planNuevo}
                onChange={(e) => setPlanNuevo(e.target.value)}
                className="entrada capitalize"
              >
                {PLANES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </label>
          }
          cambios={[{ campo: 'Plan', antes: c.plan, despues: planNuevo }]}
          aviso="Bajar de plan no borra nada: un límite más bajo impide crear de más, nunca elimina lo que el cliente ya tiene."
          pedirMotivo
          etiquetaConfirmar="Cambiar plan"
          onConfirmar={cambiarPlan}
          onCerrar={() => setAccion(null)}
        />
      )}
    </div>
  );
}

function Boton({ icono: Icono, peligro, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border ${
        peligro
          ? 'border-red-200 text-red-700 bg-white hover:bg-red-50'
          : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
      }`}
    >
      <Icono className="h-4 w-4" /> {children}
    </button>
  );
}

function Bloque({ titulo, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className="px-4 py-2.5 border-b border-gray-200 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {titulo}
      </div>
      <div className="divide-y divide-gray-100">{children}</div>
    </div>
  );
}

function Dato({ etiqueta, valor }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
      <span className="text-gray-500">{etiqueta}</span>
      <span className="font-medium text-gray-900 text-right">{valor}</span>
    </div>
  );
}

function Aviso({ tono, children }) {
  const clases = {
    rojo: 'bg-red-50 border-red-200 text-red-800',
    gris: 'bg-gray-50 border-gray-200 text-gray-700',
  };
  return (
    <div className={`text-sm border rounded-lg p-3 leading-relaxed ${clases[tono]}`}>
      {children}
    </div>
  );
}
